import { useContext, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { useNavigate } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import { InfoCard, CustomButton, SnackBar } from "src/design";
import {
  useSubscriptionTier,
  useUnsubscribeRestaurant,
} from "src/data/subscription/subscriptionTier";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import {
  isOrganizationManageEnabled,
  isPaymentSurfaceEnabled,
} from "src/helpers/FeatureCapabilityConfig";
import { upgradeableTiers } from "src/business/payment/upgradeTarget";
import { tierPerkKeys } from "src/business/subscription/tierPerks";
import { PaymentType } from "src/business/payment/payment";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { DeclarePaymentDialog } from "src/components/Main/Pages/Payments/components/DeclarePaymentDialog";
import {
  colorAccent,
  colorActionPrimaryText,
  colorBorder,
  colorSurfaceSuccess,
  colorBorderSuccess,
  colorTextPrimary,
  colorTextMuted,
  colorTextSuccess,
  colorTextError,
  radiusCard,
  radiusChip,
} from "src/design/tokens";

interface SubscriptionCardProps {
  restaurantId: string;
}

const Row = ({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      py: 2,
      borderTop: `1px solid ${colorBorder}`,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ color: colorTextMuted, display: "flex" }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 600, color: colorTextPrimary }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: colorTextMuted }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  </Box>
);

export const SubscriptionCard = ({ restaurantId }: SubscriptionCardProps) => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  const canManage = isOrganizationManageEnabled(user);
  // Reading and updating the subscription record is open to assigned staff
  // holding the organization permissions; paying for it is not. A viewer
  // without payment authorization still sees the card, just not these two
  // actions — offering them would only earn a 403.
  const canPay = isPaymentSurfaceEnabled(user);
  const navigate = useNavigate();
  const { restaurants } = useContext(RestaurantContext);
  // Which declaration flow is open, or `null` for none. Both actions on this
  // card open the same dialog the Payments page uses, so the restaurateur never
  // has to leave the page they were already reading their plan on.
  const [declareType, setDeclareType] = useState<PaymentType | null>(null);
  const {
    data: subscription,
    isLoading,
    isError,
  } = useSubscriptionTier(restaurantId);
  const { data: catalog } = useGetSubscriptionTiers();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const unsubscribeMutation = useUnsubscribeRestaurant(
    restaurantId,
    () => {
      setConfirmOpen(false);
      setToast({
        open: true,
        message: t("configuration.subscription.unsubscribeSuccess"),
        severity: "success",
      });
    },
    () =>
      setToast({
        open: true,
        message: t("configuration.subscription.unsubscribeError"),
        severity: "error",
      }),
  );

  // The tier's display name, price, and benefits are resolved from the public
  // catalog by matching the restaurant's tier id to a plan name.
  const plan = catalog?.find((p) => p.name === subscription?.tier);
  const canUpgrade =
    upgradeableTiers(catalog, subscription?.tier ?? null).length > 0;

  const restaurantName =
    restaurants.find((entry) => entry.id === restaurantId)?.name ?? "";

  // A 409 means a payment is already awaiting review. This card cannot show it,
  // so hand the viewer to the page that can rather than leaving them staring at
  // a form that will keep being refused.
  const showBlockingPayment = () => {
    setDeclareType(null);
    navigate(`/restaurant/${restaurantId}/payments`);
  };
  // The catalog sells the plan in its own words: every line of copy is a
  // translation key the backend hands over, so a repriced or re-worded tier
  // needs no change here.
  const benefits = plan ? tierPerkKeys(plan).map((key) => t(key)) : [];

  return (
    <>
      <InfoCard
        title={t("configuration.subscription.title")}
        subtitle={t("configuration.subscription.subtitle")}
        icon={<WorkspacePremiumOutlinedIcon sx={{ color: colorAccent }} />}
        action={
          canPay && canUpgrade ? (
            <CustomButton
              color="black"
              onClick={() => setDeclareType("UPGRADE")}
            >
              {t("configuration.subscription.upgrade")}
            </CustomButton>
          ) : undefined
        }
      >
        {isLoading && (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <Typography variant="body2" sx={{ color: colorTextMuted }}>
              …
            </Typography>
          </Box>
        )}

        {isError && (
          <Typography variant="body2" sx={{ color: colorTextError, py: 2 }}>
            {t("configuration.subscription.loadError")}
          </Typography>
        )}

        {!isLoading && !isError && subscription === null && (
          <Typography variant="body2" sx={{ color: colorTextMuted, py: 2 }}>
            {t("configuration.subscription.noTier")}
          </Typography>
        )}

        {!isLoading && !isError && subscription && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                p: 2,
                mb: 1,
                borderRadius: radiusCard,
                backgroundColor: colorSurfaceSuccess,
                border: `1px solid ${colorBorderSuccess}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: radiusChip,
                    backgroundColor: colorAccent,
                    color: colorActionPrimaryText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BoltOutlinedIcon />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      sx={{ fontWeight: 700, color: colorTextPrimary }}
                    >
                      {t(tierTitleKey(subscription.tier), {
                        defaultValue: subscription.tier,
                      })}
                    </Typography>
                    <Chip
                      label={t("configuration.subscription.current")}
                      size="small"
                      sx={{
                        backgroundColor: colorAccent,
                        color: colorActionPrimaryText,
                        height: 20,
                        fontSize: 11,
                      }}
                    />
                  </Box>
                  {benefits.length > 0 && (
                    <Typography variant="body2" sx={{ color: colorTextMuted }}>
                      {benefits.join(" · ")}
                    </Typography>
                  )}
                </Box>
              </Box>
              {plan && (
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: colorTextSuccess }}>
                    {formatPrice(plan.monthlyPrice, i18n.language)}
                    <Typography component="span" sx={{ color: colorTextMuted }}>
                      {` ${t("landingPage.subscriptionTiers.priceSuffix")}`}
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: colorTextMuted }}>
                    {t("landingPage.subscriptionTiers.annualRate", {
                      amount: formatPrice(
                        plan.annualMonthlyPrice,
                        i18n.language,
                      ),
                    })}
                  </Typography>
                </Box>
              )}
            </Box>

            <Row
              icon={<CalendarTodayOutlinedIcon fontSize="small" />}
              title={t("configuration.subscription.nextBillingTitle")}
              subtitle={t("configuration.subscription.nextBillingSubtitle")}
              action={
                <Chip
                  label={dayjs(subscription.nextBillingCycle).format(
                    "MMMM D, YYYY",
                  )}
                  variant="outlined"
                  size="small"
                />
              }
            />

            {canPay && (
              <Row
                icon={<AutorenewOutlinedIcon fontSize="small" />}
                title={t("configuration.subscription.renewTitle")}
                subtitle={t("configuration.subscription.renewSubtitle")}
                action={
                  <CustomButton
                    color="inverted"
                    onClick={() => setDeclareType("RENEWAL")}
                  >
                    {t("configuration.subscription.renew")}
                  </CustomButton>
                }
              />
            )}

            <Row
              icon={<GppMaybeOutlinedIcon fontSize="small" />}
              title={t("configuration.subscription.cancelTitle")}
              subtitle={t("configuration.subscription.cancelSubtitle")}
              action={
                <CustomButton
                  color="inverted"
                  disabled={!canManage || unsubscribeMutation.isPending}
                  onClick={() => setConfirmOpen(true)}
                  sx={{ color: colorTextError, borderColor: colorTextError }}
                >
                  {t("configuration.subscription.unsubscribe")}
                </CustomButton>
              }
            />
          </>
        )}
      </InfoCard>

      <Dialog
        open={confirmOpen}
        onClose={() => !unsubscribeMutation.isPending && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("configuration.subscription.unsubscribeConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("configuration.subscription.unsubscribeConfirmBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setConfirmOpen(false)}
            disabled={unsubscribeMutation.isPending}
          >
            {t("configuration.subscription.unsubscribeCancel")}
          </CustomButton>
          <CustomButton
            disabled={unsubscribeMutation.isPending}
            onClick={() => unsubscribeMutation.mutate()}
            startIcon={
              unsubscribeMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ color: colorTextError, borderColor: colorTextError }}
          >
            {t("configuration.subscription.unsubscribeConfirm")}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <DeclarePaymentDialog
        open={declareType !== null}
        onClose={() => setDeclareType(null)}
        restaurantId={restaurantId}
        restaurantName={restaurantName}
        currentTier={subscription?.tier ?? null}
        catalog={catalog}
        initialType={declareType ?? "RENEWAL"}
        presetTier={null}
        onConflict={showBlockingPayment}
      />

      <SnackBar
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};
